/**
 * Dosimetry report preview — A4 page layout with snap-scroll.
 *
 * Page 1: patient info, liver volumetry, 3D viewport.
 * Page 2+: per-PV dose tables (dynamic pagination).
 * Last pages: snapshot captures (automatic + user).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { DosimetryReportPage1, type DosimetryReportData } from './dosimetry-report-page1';
import { buildDosimetrySummaryPages, PVS_PER_PAGE } from './dosimetry-summary-pages';
import { DOSIMETRY_SNAPSHOT_TEMPLATE } from '../../domain/dosimetry-template';
import { useDosimetryState } from '../../hooks/use-dosimetry-state';

import {
    buildSnapshotPages,
    computeSnapshotPageCount,
    DEFAULT_GRID_LAYOUT,
    parseIsoDate,
    ReportPreviewLayout,
    ReportSnapshotViewportsHost,
    useReportSnapshotHandler,
    type GridLayout,
} from '@/components/report';
import { useCaseMetadata } from '@/hooks/use-case-metadata';
import type { CaseMetadata } from '@/visualization';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Build initial report data from immutable case data.
 */
function buildInitialReportData(caseData: CaseMetadata): DosimetryReportData {
    const parts = [caseData.firstname, caseData.lastname].filter(Boolean);
    return {
        patientName: parts.length > 0 ? parts.join(' ') : '',
        patientId: '',
        dateOfBirth: caseData.dob ?? '',
        gender: caseData.gender ?? '',
        studyDate: parseIsoDate(caseData.createdAt, { caseId: caseData.id }),
        studyDescription: caseData.name,
    };
}

export function DosimetryReportPreview() {
    const caseData = useCaseMetadata();
    const state = useDosimetryState();

    const initialData = useMemo(() => (caseData ? buildInitialReportData(caseData) : null), [caseData]);
    const [reportData, setReportData] = useState<DosimetryReportData | null>(initialData);

    useEffect(() => {
        if (initialData !== null) {
            setReportData(initialData);
        }
    }, [initialData]);

    const handleUpdate = useCallback((patch: Partial<DosimetryReportData>) => {
        setReportData((prev) => (prev ? { ...prev, ...patch } : prev));
    }, []);

    // Dosimetry snapshots show the liver/tumour segmentation as-is (no sizing
    // measurements), so capture once per case as soon as the case and dosimetry
    // state are ready. null until then so we never capture unloaded views.
    const captureSignature = caseData && state ? caseData.id : null;

    const { templateSnapshots, userSnapshots, isCapturing, error, registerRef, toggleReportInclusion } =
        useReportSnapshotHandler(DOSIMETRY_SNAPSHOT_TEMPLATE, captureSignature);

    const [snapshotLayouts, setSnapshotLayouts] = useState<GridLayout[]>([]);
    const handleChangeSnapshotLayout = useCallback((pageIndex: number, layout: GridLayout) => {
        setSnapshotLayouts((prev) => {
            const next = [...prev];
            while (next.length <= pageIndex) next.push(DEFAULT_GRID_LAYOUT);
            next[pageIndex] = layout;
            return next;
        });
    }, []);

    const loading = !caseData || !reportData || !state;

    // Compute page count: page 1 + dosimetry summary pages + snapshot pages
    const pvsWithDose = state?.perfusedVolumes.filter((pv) => state.doseTable(pv.id) != null) ?? [];
    const dosimetryPageCount = Math.max(1, Math.ceil(pvsWithDose.length / PVS_PER_PAGE));
    const fixedPages = 1 + dosimetryPageCount;
    const snapshotPageCount = computeSnapshotPageCount(
        templateSnapshots.length,
        userSnapshots.length,
        snapshotLayouts,
        { hasError: !!error, isCapturing }
    );
    const totalPages = fixedPages + snapshotPageCount;
    const snapshotStartPage = fixedPages + 1;

    const summaryPages = state ? buildDosimetrySummaryPages(state, 2, totalPages) : [];

    return (
        <>
            {/*
             * Hidden off-screen viewport containers used for snapshot capture.
             * Must be rendered outside ReportPreviewLayout (which has overflow:hidden)
             * so that fixed positioning works correctly.
             */}
            <ReportSnapshotViewportsHost template={DOSIMETRY_SNAPSHOT_TEMPLATE} registerRef={registerRef} />

            <ReportPreviewLayout loading={loading} loadingMessage="Loading dosimetry data…">
                <DosimetryReportPage1
                    data={reportData!}
                    onUpdate={handleUpdate}
                    state={state!}
                    totalPages={totalPages}
                />
                {summaryPages}
                {buildSnapshotPages({
                    startPageNumber: snapshotStartPage,
                    totalPages,
                    templateSnapshots,
                    userSnapshots,
                    isCapturing,
                    error,
                    onToggleReportInclusion: toggleReportInclusion,
                    layouts: snapshotLayouts,
                    onChangeLayout: handleChangeSnapshotLayout,
                })}
            </ReportPreviewLayout>
        </>
    );
}
